// The report's tiles and charts. One hue for data (mixed from the ink); the
// chosen mark in full ink; ticket yellow nowhere; amber only for a figure that
// went the wrong way against the period before.
//
// Drawn here rather than pulled from a library: this product carries two
// runtime dependencies, and a chart package would be the largest thing in the
// bundle to draw three shapes. The rules that make a chart honest hold for all
// of them: one scale places the marks and the labels, every figure is written on
// its mark (a tooltip does not exist on a touchscreen), and text takes its colour
// from the theme tokens so a chart reads in the dark as well as on white.
import { Fragment, useLayoutEffect, useState, type ReactNode } from 'react'
import { Button, Popover } from '@heroui/react'
import type { StaffLanguage } from '../lib/staffText'
import { Icon } from '../ui/icons'
import { durationParts } from './insightCalc'
import { ActionMenu } from './kit'
import { useStaffText } from '../ui/StaffText'

/** A time as a headline figure: the numbers large, the units small ("9 min 28 s"). */
export function DurText({ sec, lang }: { sec: number; lang: StaffLanguage }) {
  return (
    <>
      {durationParts(sec, lang).map(([n, u], i) => (
        <Fragment key={i}>
          {i > 0 && ' '}
          {n}
          <span className="insight-unit">{'\u00a0' + u}</span>
        </Fragment>
      ))}
    </>
  )
}

export type Mark = { key: string; label: string; value: number | null; ghost?: number | null; on?: boolean; note?: string; /** The busiest of a day's slots, drawn in full ink. */ peak?: boolean }

/** The element's size, kept as it changes. A callback ref, so an element that
 *  mounts later (a chart that first said "nothing to show") is measured too. */
export function useSize<T extends HTMLElement>() {
  const [el, setEl] = useState<T | null>(null)
  const [size, setSize] = useState({ w: 480, h: 220 })
  useLayoutEffect(() => {
    if (!el) return
    const r = el.getBoundingClientRect()
    if (r.width > 0) setSize({ w: Math.round(r.width), h: Math.round(r.height) })
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => {
      if (e) setSize({ w: Math.round(e.contentRect.width), h: Math.round(e.contentRect.height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [el])
  return [setEl, size] as const
}

// ---------------------------------------------------------------- tile

export type MenuItem = { label: string; run: () => void; on?: boolean; disabled?: boolean } | 'rule'

/** One visual on the canvas: a title, a menu (export, focus, sort), the chart. */
export function Tile({
  id,
  title,
  menu,
  focused,
  onBack,
  tools,
  info,
  span,
  children,
}: {
  id: string
  title: string
  menu: MenuItem[]
  focused: boolean
  onBack: () => void
  tools?: ReactNode
  /** One sentence behind an (i), for a visual that has to say how it may be read. */
  info?: string
  /** Columns of six it takes, wide and mid width; packed by the page so the canvas has no holes. */
  span?: { wide: number; mid: number }
  children: ReactNode
}) {
  const { say } = useStaffText()
  return (
    <section
      className="insight-tile"
      data-focus={focused || undefined}
      data-tile={id}
      data-span={span ? true : undefined}
      style={span ? ({ '--span': span.wide, '--span-mid': span.mid } as React.CSSProperties) : undefined}
      aria-label={title}
    >
      <header className="insight-tile__head">
        {focused && (
          <Button variant="ghost" size="sm" onPress={onBack}>
            <Icon name="left" size={16} /> {say('common.back')}
          </Button>
        )}
        <h2 className="insight-tile__title">{title}</h2>
        {info && (
          <Popover>
            <Button isIconOnly variant="ghost" size="sm" aria-label={say('insight.about')}>
              <Icon name="info" size={16} />
            </Button>
            <Popover.Content placement="bottom end" className="rp-pop">
              <Popover.Dialog aria-label={title}>
                <p className="insight-info">{info}</p>
              </Popover.Dialog>
            </Popover.Content>
          </Popover>
        )}
        {tools}
        <ActionMenu
          label={say('insight.options')}
          icon="more"
          iconOnly
          variant="ghost"
          items={menu.map((m, i) => (m === 'rule' ? 'rule' : { id: String(i), label: m.label, icon: m.on ? 'check' : undefined, disabled: m.disabled, run: m.run }))}
        />
      </header>
      <div className="insight-tile__body">{children}</div>
    </section>
  )
}

/** The key to the faint marks behind the bars: the period before. */
export function BeforeKey() {
  const { say } = useStaffText()
  return (
    <p className="insight-legend" aria-hidden="true">
      <span className="insight-legend__swatch" />
      {say('insight.before')}
    </p>
  )
}

export function NoData({ children }: { children?: ReactNode }) {
  const { say } = useStaffText()
  return <p className="insight-nodata">{children ?? say('reports.nothingToShow')}</p>
}

// ---------------------------------------------------------------- bars

/** Ranked horizontal bars: which service, which desk. Horizontal because the
 *  labels are words. `max` fixes the scale where it is a promise rather than the
 *  data — a service level is measured against 100%, not against the best one. */
export function Bars({
  marks,
  fmt,
  after,
  onPick,
  label,
  max,
}: {
  marks: Mark[]
  fmt: (n: number | null) => string
  after?: (m: Mark) => ReactNode
  onPick?: (key: string) => void
  label: string
  max?: number
}) {
  if (marks.length === 0) return <NoData />
  const top = max ?? Math.max(0.0001, ...marks.map((m) => Math.max(m.value ?? 0, m.ghost ?? 0)))
  const pct = (v: number) => `${Math.min(100, (v / top) * 100).toFixed(2)}%`
  const ghosts = marks.some((m) => (m.ghost ?? 0) > 0)
  return (
    <>
      <div className="insight-bars" role="group" aria-label={label}>
        {marks.map((m) => (
          <button
            key={m.key}
            type="button"
            className="insight-bar"
            data-dim={m.on === false || undefined}
            data-on={m.on || undefined}
            aria-pressed={onPick ? !!m.on : undefined}
            disabled={!onPick}
            onClick={onPick ? () => onPick(m.key) : undefined}
          >
            <span className="insight-bar__name">
              <span>{m.label}</span>
            </span>
            <span className="insight-bar__val">
              {fmt(m.value)}
              {after?.(m)}
            </span>
            <span className="insight-bar__track">
              {m.ghost != null && <span className="insight-bar__ghost" style={{ width: pct(m.ghost) }} />}
              <span className="insight-bar__fill" style={{ width: m.value ? pct(m.value) : 0 }} />
            </span>
          </button>
        ))}
      </div>
      {ghosts && <BeforeKey />}
    </>
  )
}
